import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  ButtonGroup, Card, CircularProgress, Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { useDispatch } from '../store'
import { PrivatePage } from '../component/PagePrivate'
import { PopupNotify } from '../component/PopupNotify'
import { Subscriber, SubscriberStatus } from '../store/user/reducer'
import { useParams } from 'react-router-dom'
import { getSingleSubscriber, toggleBlock } from '../store/user/actionCreator'
import { EnumSubscriberStatus } from '../enum/statusSubscriber'
import { TableStatus } from '../component/datatable/component'
import { SvgIcon } from '../component/SvgIcon'
import { colorPrimary } from '../mui'
import dashboard_ from '../sass/dashboard.module.sass'
import { PackageBlock } from '../container/dashboard/BlockPackage'
import { SubscriberTickets } from '../container/dashboard/SubscriberTickets'
import { UsageBreakdown } from '../container/dashboard/UsageBreakdown'
import { CardContent, CardHeader } from "@mui/material"
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import LookupsBlock from '../component/subscriber/LookupsBlock'
import { getSubscriber } from '../store/common/actionCreator'
import { PopupAsk } from '../component/PopupAsk'
import { SubscriberRequests } from '../component/subscriber/SubscriberRequests'
import { AdditionalPackage } from '../container/dashboard/AdditionalPackage'
import { MainInfo } from '../component/subscriber/MainInfo'
import Calendar from '../component/calendar/Calendar'
import { InvoicesTable } from '../component/invoices/InvoicesTable'
import { AIAssistant } from '../component/AIAssistant/AIAssistant'
import { AdminsActions } from '../component/admin_logs/AdminsActions'
import IconButton from '@mui/material/IconButton'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

export function SubscriberPage() {
  const { t } = useTranslation()
  const { id } = useParams() as {
    id: string;
  }
  const dispatch = useDispatch()
  const [subscriber, setSubscriber] = React.useState<Subscriber>({} as Subscriber)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [emailOpen, setEmailOpen] = React.useState(false)
  const [blockingId, setBlockingId] = React.useState<number>()
  const [initialStatus, setInitialStatus] = React.useState<SubscriberStatus>()
  
  //callbacks
  const dispatchSingleSubscriber = React.useCallback((id: number) => {
    setLoading(true)
    dispatch(getSingleSubscriber(id, (data: Subscriber) => {
      setSubscriber(data)
      setLoading(false)
      setInitialStatus(data.status)
    }))
  }, [dispatch])

  function blockCallback() {
    setSubscriber({ ...subscriber, isBlocked: true, status: EnumSubscriberStatus.BLOCKED })
  }

  const [partnersPage, setPartnersPage] = useState(0)
  const rowsPerPage = 5

  const partners = subscriber?.partners || []

  const paginatedPartners = partners.slice(
    partnersPage * rowsPerPage,
    partnersPage * rowsPerPage + rowsPerPage,
  )

  const handlepartnerClick = (id: number) => {
    window.open(`/users/subscriber/${id}`, '_blank', 'noopener,noreferrer')
    return false
  }

  interface StateProperties {
    isBlocked: boolean,
    status: SubscriberStatus
  }

  function unblockCallback() {
    const updatedData: StateProperties = { isBlocked: false, status: EnumSubscriberStatus.TRIAL }
    if (initialStatus) {
      updatedData.status = initialStatus
    }
    setSubscriber({ ...subscriber, ...updatedData })
  }

  //effects
  React.useEffect(() => {
    dispatchSingleSubscriber(Number(id))
  }, [id, dispatchSingleSubscriber])

  React.useEffect(() => {
    dispatch(getSubscriber(Number(id), 0, 100, 'all', 'year'))
  }, [id, dispatch])


  const today = new Date()

  return (
    <PrivatePage>
      {
        loading && <CircularProgress />
      }
      {
        subscriber.id && <Card>
          <Stack direction="column" width="100%">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
              mb={3}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="semiBold" fontSize={36} lineHeight={1}>
                  {t('subscriber.title.popup')}
                </Typography>
                {subscriber.status !== EnumSubscriberStatus.DELETED && (
                  <TableStatus status={subscriber.status} />
                )}
              </Stack>

              <ButtonGroup variant="outlined" color="secondary">
                {subscriber.isDeleted ? (
                  <Button
                    className={dashboard_.button__deleted}
                    variant="contained"
                    color="error"
                  >
                    IS DELETED
                  </Button>
                ) : subscriber.isBlocked ? (
                  <Button
                    sx={{ padding: '0 15px', borderRadius: '20px' }}
                    onClick={() =>
                      dispatch(toggleBlock(subscriber.id, false, unblockCallback))
                    }
                  >
                    <SvgIcon
                      name="block"
                      color={colorPrimary}
                      size={{ width: 18, height: 18 }}
                    />
                    <Typography variant="semiBold" color="primary" ml={1}>
                      {t('common.unblock')}
                    </Typography>
                  </Button>
                ) : (
                  <Button
                    sx={{ padding: '0 15px', borderRadius: '20px' }}
                    onClick={() => setBlockingId(subscriber.id)}
                  >
                    <SvgIcon
                      name="block"
                      color={colorPrimary}
                      size={{ width: 18, height: 18 }}
                    />
                    <Typography variant="semiBold" color="primary" ml={1}>
                      {t('common.block')}
                    </Typography>
                  </Button>
                )}
              </ButtonGroup>
            </Stack>

            <Stack direction="column">
              <Stack spacing={2}>
                <Card variant="outlined">
                  <CardHeader />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label="ID" value={subscriber.id} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.since")} value={subscriber.createdTime} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.name")} value={`${subscriber.firstname || ''} ${subscriber.lastname || ''}`} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.country")} value={subscriber.country} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.phoneNumber")} value={subscriber.phone?.replace('+', '')} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.emailAddress")} value={subscriber.email} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("Autorenewal")} value={subscriber.isAutoRenewal} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("Autorefill")} value={subscriber.isAutoRefill} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.phone_valid")}
                          value={subscriber?.registerInfo?.phone_valid} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.email_is_disposable_email")}
                          value={subscriber?.registerInfo?.email_is_disposable_email} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.email_quality_score")} value={subscriber?.registerInfo?.email_quality_score} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.handlIP")} value={subscriber?.userAgent?.handlIP} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.isIpPhoneMatches")} value={subscriber?.userAgent?.isIpPhoneMatches} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.isProxy")} value={subscriber?.userAgent?.isProxy} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.ipMatchesCount")} value={subscriber?.userAgent?.ipMatchesCount} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.partners")} value={subscriber?.partners?.length} />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="semiBold" fontSize={18} component="h2">{t("subscriber.email_phone_section")}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.email_deliverability")} value={subscriber?.registerInfo?.email_deliverability} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.email_is_smtp_valid")} value={subscriber?.registerInfo?.email_is_smtp_valid} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.phone_type")} value={subscriber?.registerInfo?.phone_type} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.utmCampaign")} value={subscriber?.userAgent?.utmCampaign} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.utmSource")} value={subscriber?.userAgent?.utmSource} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.utmTerm")} value={subscriber?.userAgent?.utmTerm} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.utmContent")} value={subscriber?.userAgent?.utmContent} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.utmMedium")} value={subscriber?.userAgent?.utmMedium} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.gaClientId")} value={subscriber?.userAgent?.gaClientId} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <MainInfo label={t("subscriber.handlCustom1")} value={subscriber?.userAgent?.handlCustom1} />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
                
                { subscriber?.partners && Array.isArray(subscriber?.partners) && subscriber?.partners.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="semiBold" fontSize={18} component="h2">{t("subscriber.partners")}</Typography>
                  </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Partner ID</TableCell>
                              <TableCell>API Key</TableCell>
                              <TableCell>Created</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Action</TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            {paginatedPartners.map((partner: any) => (
                              <TableRow key={partner.id}>
                                <TableCell>
                                  <Typography fontWeight={600}>{partner.id}</Typography>
                                </TableCell>

                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <VpnKeyIcon fontSize="small" />
                                    <Typography variant="body2">
                                      {partner.apiKey?.key || 'No API key'}
                                    </Typography>
                                  </Box>
                                </TableCell>

                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {partner.apiKey?.createdTime
                                      ? new Date(partner.apiKey.createdTime).toLocaleString()
                                      : '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color={partner.isDeleted ? 'error.main' : 'success.main'}
                                  >
                                    {partner.isDeleted ? 'Deleted' : 'Active'}
                                  </Typography>
                                </TableCell>

                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlepartnerClick(partner.id)}
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>

                        <TablePagination
                          component="div"
                          count={partners.length}
                          page={partnersPage}
                          onPageChange={(_, newPage) => setPartnersPage(newPage)}
                          rowsPerPage={rowsPerPage}
                          rowsPerPageOptions={[5]}
                        />
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                ) }
              
              </Stack>
              <Grid container rowSpacing={3} mt={3}>
                <Grid item xs={12}>
                  <PackageBlock data={subscriber} />
                </Grid>
                <Grid item xs={12}>
                  <AdditionalPackage data={subscriber} />
                </Grid>
                <Grid item xs={12}>
                  <LookupsBlock data={subscriber} />
                </Grid>
                {/* <Grid item xs={12}>
                    <UserBlock data={subscriber} />
                    </Grid> */}
              </Grid>

              <Stack spacing={2}>
              <Grid container spacing={3} mt={1}>
                <Grid item xs={6}>
                  <SubscriberTickets data={subscriber} />
                </Grid>
                <Grid item xs={3}>
                  <Grid item xs={12}>
                    <Calendar subscriber={subscriber} />
                  </Grid>
                </Grid>
                <Grid item xs={3}>
                  <Grid item xs={12}>
                    <AIAssistant subscriber={subscriber}  isMinimized={false} isMobile={false}/>
                  </Grid>
                </Grid>
              </Grid>

                <Grid item xs={3}>
                  <Grid item xs={12}>
                    <UsageBreakdown data={subscriber} />
                  </Grid>
                </Grid>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="semiBold" fontSize={18} component="h2">{t("subscriber.invoices")}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3} mt={1}>
                    <Grid item xs={12}>
                      <InvoicesTable subscriber={subscriber} />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="semiBold" fontSize={18} component="h2">{t("subscriber.admin_actions")}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3} mt={1}>
                    <Grid item xs={12}>
                      <AdminsActions subscriber={subscriber} />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
              </Stack>

              <Grid container spacing={3} mt={1}>
                <Grid item xs={12}>
                  <SubscriberRequests data={subscriber} />
                </Grid>
              </Grid>
            </Stack>
          </Stack>
        </Card>
        // <LookupsBlock data={subscriber} />
      }
      {blockingId && (
        <PopupAsk
          onYes={() => dispatch(toggleBlock(subscriber.id, true, blockCallback))}
          onClose={() => setBlockingId(undefined)}
        />
      )}
      {emailOpen && (
        <PopupNotify
          mode="email"
          subscribers={[subscriber]}
          onClose={() => setEmailOpen(false)}
        />
      )}
    </PrivatePage>
  )
}
